import { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useSelector } from 'react-redux';
import { TrashIcon } from '@pmi/dsm-react/dist/components/icons/fa/24';
import { Form } from 'react-bootstrap';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import * as Yup from 'yup';
import { Dropdown, LinkButton, Modal, TextInput } from '@pmi/dsm-react';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import { SelectOption } from '../../../../../../Hub/TrainingHub/EnrollmentCard/ProductModal/ProfileUpdateModal/models';
import { accountSelector } from '../../../../../../shared/store/shared/accountSlice';
import { useUpdateUser2FANumberMutation } from './apiSlice';
import { User2FAPhoneNumberModel } from '../../../../../../shared/models/UserModels';
import DeleteUser2FANumberModal from '../DeleteUserPhoneNumberModal';
import styles from './styles.module.scss';

interface EditPhoneNumberModalProps {
  accountUserId: string;
  countries: SelectOption[];
  phoneNumber?: User2FAPhoneNumberModel;
  onCancel?: () => void;
  phoneNumberSchema: any;
}

interface FormModel {
  phoneNumber: User2FAPhoneNumberModel;
  accountUserId: string;
}

export default function EditPhoneNumberModal({
  accountUserId,
  phoneNumber = undefined,
  phoneNumberSchema,
  countries,
  onCancel = () => {},
}: EditPhoneNumberModalProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const [showDeletePhoneNumberModal, setShowDeletePhoneNumberModal] =
    useState(false);

  const [updateUserPhoneNumber, { isLoading }] =
    useUpdateUser2FANumberMutation();
  const countryRegionCodeOptions: Option[] =
    countries
      ?.map((pn) => ({
        label: pn.text,
        value: pn.value,
      }))
      .filter((c) =>
        ['canada', 'india', 'united states', 'united kingdom'].some(
          (n) =>
            c?.label.toLowerCase().length > n.length &&
            c?.label.toLowerCase().substring(0, n.length) === n,
        ),
      ) ?? [];
  const [options, setOptions] = useState(countryRegionCodeOptions);

  const initialData: FormModel = {
    phoneNumber,
    accountUserId,
  };
  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.object().shape({
      countryCodeId: Yup.string().required('Country/Region code is required'),
      number: Yup.string()
        .required('Phone number is required')
        .matches(
          phoneNumberSchema?.properties?.number?.pattern,
          'Must be a valid phone number',
        ),
    }),
  });

  const handleInputChange = (_: any, value: string, currentValue?: string) => {
    const newOptions = countryRegionCodeOptions.filter(
      (option) =>
        option.label
          .toLocaleLowerCase()
          .startsWith(value.toLocaleLowerCase()) ||
        option.value === currentValue,
    );
    setOptions(newOptions);
  };

  const onSave = async (
    values: FormModel,
    action: FormikHelpers<FormModel>,
  ) => {
    action.setSubmitting(true);

    const countryCode = countryRegionCodeOptions
      .find(
        (x) =>
          x.value.toString() === values.phoneNumber.countryCodeId.toString(),
      )
      .label.split(' ')
      .pop();

    updateUserPhoneNumber({
      accountId: currentAccountId,
      accountUserId,
      phoneNumberRequest: {
        ...values.phoneNumber,
        countryCode,
      },
    })
      .unwrap()
      .then(() => {
        toastWithAnalytics(`Phone number updated successfully`, 'success');
        action.setSubmitting(false);
        onCancel();
      })
      .catch((ex) => {
        toastWithAnalytics(
          `Error while updating phone Number !! ${JSON.stringify(
            ex?.data?.errors,
          )}.`,
          'danger',
        );
      })
      .finally(() => {
        action.setSubmitting(false);
      });
  };

  const onCancelHandler = () => {
    onCancel();
  };

  return (
    <>
      <Formik
        initialValues={initialData}
        validateOnChange
        validateOnBlur
        validationSchema={validationSchema}
        onSubmit={(values, action) => {
          onSave(values, action);
        }}
      >
        {({
          submitForm,
          dirty,
          errors,
          setFieldValue,
          handleChange,
          values,
          initialValues,
          isValid,
          isSubmitting,
        }) => (
          <Modal
            show
            customClassName="modalBody100"
            headline={`${phoneNumber ? 'Edit' : 'Add'} Phone Number`}
            description="To enable SMS Text Two-Factor Authentication (2FA) for this User, please enter a valid phone number below.  Phone number must reside in one of the approved countries to function."
            onClose={onCancelHandler}
            buttons={[
              {
                titleText: 'Cancel',
                variant: 'outline-secondary',
                onClick: onCancelHandler,
                disabled: isLoading,
              },
              {
                titleText: 'Save Changes',
                variant: 'primary',
                onClick: submitForm,
                disabled:
                  !dirty ||
                  !isValid ||
                  values === initialValues ||
                  isLoading ||
                  isSubmitting,
              },
            ]}
          >
            <div className="d-flex flex-column mb-5">
              <Form className="mb-5">
                <div className="row gy-3 row-cols-1 mb-5">
                  <div className="col-6 mb-3">
                    <Form.Group
                      controlId="phoneNumber.countryCodeId"
                      className={
                        errors?.phoneNumber?.countryCodeId ? 'is-invalid' : null
                      }
                    >
                      <Dropdown
                        className="cursor-pointer"
                        onChange={(e, value) => {
                          setFieldValue('phoneNumber.countryCodeId', value);
                          setOptions(countryRegionCodeOptions);
                        }}
                        onChangeInput={(_, value) =>
                          handleInputChange(
                            _,
                            value,
                            values.phoneNumber?.countryCodeId,
                          )
                        }
                        label="Country/Region Code"
                        options={options}
                        invalid={!!errors.phoneNumber?.countryCodeId}
                        placeholder="Select"
                        value={values.phoneNumber?.countryCodeId?.toString()}
                      />
                      {errors.phoneNumber?.countryCodeId ? (
                        <div className="invalid-feedback">
                          {errors.phoneNumber?.countryCodeId}
                        </div>
                      ) : null}
                    </Form.Group>
                  </div>

                  <div className="col-6 mb-3">
                    <Form.Group
                      controlId="phoneNumber.number"
                      className={
                        errors?.phoneNumber?.number ? 'is-invalid' : null
                      }
                    >
                      <Form.Label className="mb-2">Number</Form.Label>
                      <TextInput
                        name="phoneNumber.number"
                        maxLength={
                          phoneNumberSchema?.properties?.number?.maxLength
                        }
                        onChange={handleChange}
                        value={values?.phoneNumber?.number}
                        isInvalid={!!errors?.phoneNumber?.number}
                        className={
                          errors?.phoneNumber?.number ? 'is-invalid' : null
                        }
                        validationMessage={errors?.phoneNumber?.number}
                      />
                    </Form.Group>
                  </div>
                  <div className="col mt-0 ">
                    {phoneNumber && phoneNumber.number && (
                      <LinkButton
                        className={`${styles.deleteLink} p-0 color-danger h-auto ms-auto`}
                        variant="light"
                        titleText="Remove"
                        icon={TrashIcon}
                        onClick={(e: any) => {
                          e.preventDefault();
                          setShowDeletePhoneNumberModal(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              </Form>
            </div>
          </Modal>
        )}
      </Formik>

      {showDeletePhoneNumberModal && (
        <DeleteUser2FANumberModal
          accountUserId={accountUserId}
          onSuccess={() => {
            onCancel();
            setShowDeletePhoneNumberModal(false);
          }}
          onCancel={() => setShowDeletePhoneNumberModal(false)}
        />
      )}
    </>
  );
}
